import React, { useState, useEffect } from 'react';
import * as htmlToImage from 'html-to-image';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const DownloadButton = ({ image, position, names, isGenerating, setIsGenerating, fontSettings, isWebP }) => {
  const hiddenPreviewRef = React.useRef(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Load image dimensions properly
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.src = image;
  }, [image]);

  const generateImage = async (name) => {
    const previewNode = hiddenPreviewRef.current;
    const nameElement = previewNode.querySelector('.preview-name');
    
    // Apply current settings dynamically
    nameElement.style.fontFamily = fontSettings.family;
    nameElement.style.fontFace = fontSettings.family;
    nameElement.style.fontSize = `${fontSettings.size * 4}px`;
    nameElement.style.color = fontSettings.color;
    nameElement.textContent = name;

    if(fontSettings.customFonts.length > 0) {
      const font = new FontFace(fontSettings.family, `url(${fontSettings.customFonts[0].src})`);
      await font.load();
      document.fonts.add(font);
      const style = document.createElement('style');
style.textContent = `
  @font-face {
    font-family: '${fontSettings.customFonts[0].name}';
    src: url(${fontSettings.customFonts[0].src}) format('truetype');
  }
`;
document.head.appendChild(style);
    }

    // Ensure fonts are loaded
    try {
      await document.fonts.ready;
      if (!document.fonts.check(`1em "${fontSettings.family}"`)) {
        await document.fonts.load(`1em "${fontSettings.family}"`);
      }
      await new Promise(resolve => requestAnimationFrame(resolve));
    } catch (error) {
      console.error('Font loading failed:', error);
      throw new Error('Font loading failed');
    }

    // Use exact dimensions
    return htmlToImage.toPng(previewNode, {
        width: imageDimensions.width,
        height: imageDimensions.height,
        fontFamily: fontSettings.family,
        pixelRatio: 2,
        cacheBust: true,
        style: {
          transform: 'none',
          visibility: 'visible'
        }
      });
    };

  const convertToWebP = async (blob, quality) => {

    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const webpBlob = new Blob([uint8Array], { type: 'image/webp' });
    return webpBlob;

  }

  const handleDownload = async () => {
    if (!imageDimensions.width || names.length === 0) return;
    setIsGenerating(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder('invitations');
      
      for (const name of names) {
        const dataUrl = await generateImage(name);
        const blob = await fetch(dataUrl).then(r => r.blob());
        if(isWebP) {
          const webpBlob = await convertToWebP(blob, 0.8);
          folder.file(`${name.replace(/[^a-z0-9]/gi, '_')}.webp`, webpBlob);
        } else {
          folder.file(`${name.replace(/[^a-z0-9]/gi, '_')}.png`, blob);
        }
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'invitations.zip');
    } catch (error) {
      console.error('Error generating images:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="w-full">
      <button
        onClick={handleDownload}
        disabled={isGenerating || names.length === 0}
        className="w-full py-3 px-6 bg-accent hover:bg-accent-dark text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isGenerating ? 'Generating ZIP...' : 'Download All Invitations'}
      </button>
      
      {/* Hidden preview for image generation */}
      <div className="fixed -left-[9999px]">
        <div ref={hiddenPreviewRef} className="relative" style={{
            width: `${imageDimensions.width}px`,
            height: `${imageDimensions.height}px`,
            position: 'relative'
          }}>
          <img 
            src={image} 
            alt="Template" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          />
          <div
            className="preview-name absolute"
            style={{
              position: 'absolute',
              left: `${position.x}%`,
              top: `${position.y}%`,
              transform: 'translate(-50%, -50%)',
              whiteSpace: 'nowrap',
              margin: 0,
              padding: 0
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DownloadButton;